/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 * @description STR List Item
 */
/** @jsx jsx */
import { jsx } from '@emotion/react'
import { selectLongTerm } from '@zcode-ui/filters'
import type { Origin } from '@zcode/nr-utils'
import { AnalyticsC } from '@zcode/nr-utils'
import { bookingProviders } from '@zcode/nr/es/shared/constants/source'
import type { Listable } from '@zcode/state-type'
import { type KeyboardEvent, type MouseEvent, memo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useSelector } from 'react-redux'
import { ListCardBadges } from '../../../../ListCardBadge/ListCardBadges'
import { FavoriteContainer } from '../../../../favorite/FavoriteContainer'
import { ListItemDesktopViewContent } from '../ListItem/DesktopView/ListItemDesktopViewContent'
import { useTrackImpression } from '../ListItem/hooks/useTrackImpression'
import { CarouselWrapper } from './CarouselWrapper'
import { ContentWrapper } from './ContentWrapper/ContentWrapper'
import { STRListItemCard } from './STRListItemCard'
import * as styles from './styles'

export interface STRListItemProps {
  analyticChannel: (typeof AnalyticsC)['LIST_ITEM_MESSAGED'] | (typeof AnalyticsC)['LIST']
  bookingProviderHref: string
  entity: Listable
  forceIntersected?: boolean
  index: number
  isInitialLocationChanged: boolean
  onClick: (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => void
  onClickMoreAmenities: () => void
  onClickQuickLook: (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => void
  onClickThirdPartyListCard: () => void
  onClickout: () => void
  onMouseEnter: (event: MouseEvent<HTMLElement>) => void
  onMouseLeave: (event: MouseEvent<HTMLElement>) => void
  origin: Origin
  page: number
  shouldPreloadImage?: boolean
  stayLength?: number
  trackListItemImpression: () => void
}

export const STRListItem = memo(
  ({
    analyticChannel,
    bookingProviderHref,
    entity,
    forceIntersected,
    index,
    isInitialLocationChanged,
    onClick,
    onClickMoreAmenities,
    onClickThirdPartyListCard,
    onClickout,
    onMouseEnter,
    onMouseLeave,
    origin,
    page,
    shouldPreloadImage,
    stayLength,
    trackListItemImpression,
  }: STRListItemProps) => {
    const [showInfo, setShowInfo] = useState(false)
    const [ref, inView] = useInView({ threshold: 0.95 })
    useTrackImpression({
      entity,
      index,
      inView,
      onImpression: trackListItemImpression,
    })
    const { feedName, hasImage, images } = entity
    const isLongTerm = useSelector(selectLongTerm)
    const providers: readonly string[] = bookingProviders
    const shouldOpenThirdParty = !!feedName && providers.includes(feedName) && !isLongTerm

    const handleOnClick = (e: MouseEvent<HTMLElement>) => {
      if (shouldOpenThirdParty && isInitialLocationChanged) {
        onClickThirdPartyListCard()
      } else {
        onClick(e)
      }
    }

    return (
      <STRListItemCard
        isLongTerm={!!isLongTerm}
        onClick={handleOnClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={ref}
      >
        <div
          css={[
            styles.imageSection,
            styles.gridImageSection,
            !isLongTerm && showInfo && styles.hideSection,
          ]}
        >
          <CarouselWrapper
            entity={entity}
            forceIntersected={forceIntersected}
            groupId={entity.groupId}
            hasImage={hasImage}
            images={images}
            shouldPreloadImage={shouldPreloadImage}
          />
          <div css={styles.badges}>
            <ListCardBadges entity={entity} />
          </div>
          <div css={styles.infoFavorite}>
            <FavoriteContainer
              entity={entity}
              isRoundButton
              parentName={AnalyticsC.LIST_ITEM}
              size={16}
            />
          </div>
        </div>
        <div css={[styles.contentSection, styles.gridContentSection]}>
          {isLongTerm ? (
            <ListItemDesktopViewContent
              analyticChannel={analyticChannel}
              entity={entity}
              origin={origin}
              setShowInfo={setShowInfo}
              showInfo={showInfo}
            />
          ) : (
            <ContentWrapper
              bookingProviderHref={bookingProviderHref}
              entity={entity}
              index={index}
              isInitialLocationChanged={isInitialLocationChanged}
              onClick={onClick}
              onClickMoreAmenities={onClickMoreAmenities}
              onClickout={onClickout}
              page={page}
              setShowInfo={setShowInfo}
              showClickOutCTA={shouldOpenThirdParty}
              showInfo={showInfo}
              stayLength={stayLength}
            />
          )}
        </div>
      </STRListItemCard>
    )
  }
)
